/* eslint-disable roros/no-raw-html-in-routes */
// reason: pre-existing <button>/<input>/<select> elements that pre-date the LINT-1 (AILLM-3) rule extension. New code must use components/ui/*. Migration tracked separately.
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageContainer, Button, Card, CardContent, CardHeader, CardTitle, CardDescription, Switch } from '@/components/ui';
import { usePageHeader } from '@/hooks/usePageHeader';
import { SimpleTooltip } from '@/components/ui/Tooltip';
import { Textarea } from '@/components/ui/Textarea';
import { useOrgStore } from '@/stores/orgStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { OrganizationSettingsService } from '@/services/OrganizationSettingsService';
import { apiFetch } from '@/lib/api';
import { Loader2, Save, Settings, Share2, HelpCircle } from 'lucide-react';
import { t } from '@/lib/i18n';

const settingsService = new OrganizationSettingsService();

export function GeneralSettings() {
  usePageHeader({ title: t('General Settings'), breadcrumbs: [{ label: t('Organization'), path: '/organization' }] });

  const { activeOrgId, orgs, fetchOrgs } = useOrgStore();
  const queryClient = useQueryClient();
  const notificationStore = useNotificationStore();

  // Get current org from the orgs list (includes share_config_with_admin)
  const org = orgs.find((o: any) => o.id === activeOrgId) as any;

  const [notificationEmails, setNotificationEmails] = useState('');

  // Fetch settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['organization-settings', activeOrgId],
    queryFn: () => settingsService.getSettings(activeOrgId!),
    enabled: !!activeOrgId,
  });

  // Update form when settings load
  useEffect(() => {
    if (settings) {
      setNotificationEmails(settings.notification_emails || '');
    }
  }, [settings]);

  // Share config toggle mutation
  const shareConfigMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const res = await apiFetch(`/api/orgs/${encodeURIComponent(activeOrgId!)}/share-config`, {
        method: 'PUT',
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: { message: 'Failed to update sharing preference' } }));
        throw new Error(err.error?.message || 'Failed to update sharing preference');
      }
      return res.json();
    },
    onSuccess: () => {
      // Refresh orgs list in the store so the UI reflects the new share_config_with_admin value
      fetchOrgs();
      notificationStore.success(t('Sharing preference updated'));
    },
    onError: (error: any) => {
      notificationStore.error(error.message || t('Failed to update sharing preference'));
    },
  });

  // Update settings mutation
  const updateMutation = useMutation({
    mutationFn: (data: { notification_emails?: string | null }) =>
      settingsService.updateSettings(activeOrgId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-settings', activeOrgId] });
      notificationStore.success(t('Settings updated successfully'));
    },
    onError: (error: any) => {
      notificationStore.error(error.message || t('Failed to update settings'));
    },
  });

  const handleSave = () => {
    updateMutation.mutate({
      notification_emails: notificationEmails || null,
    });
  };

  const hasChanges =
    notificationEmails !== (settings?.notification_emails || '');

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="flex items-center justify-end mb-4 gap-2">
        <Button
          onClick={handleSave}
          disabled={!hasChanges || updateMutation.isPending}
          isLoading={updateMutation.isPending}
          leftIcon={<Save className="w-4 h-4" />}
        >
          Save Changes
        </Button>
      </div>

      <div className="mt-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary-600" />
              Notification Settings
            </CardTitle>
            <CardDescription>
              Configure notification preferences for your organization
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <label className="block text-sm font-medium mb-1">
                Notification Emails
              </label>
              <Textarea
                value={notificationEmails}
                onChange={(e) => setNotificationEmails(e.target.value)}
                placeholder="email1@example.com, email2@example.com"
                rows={3}
                hint="Comma-separated list of email addresses to receive organization notifications."
              />
            </div>
          </CardContent>
        </Card>

        {/* Share Config with Platform Admin */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-primary-600" />
              Platform Admin Sharing
            </CardTitle>
            <CardDescription>
              Allow the platform administrator to view and help manage your organization settings
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">
                  Share settings with platform admin
                </span>
                <SimpleTooltip
                  content="When enabled, the platform administrator can view and help manage your AI configuration, theme settings, and organization profile."
                  side="right"
                >
                  <button type="button" className="text-muted-foreground hover:text-foreground">
                    <HelpCircle className="w-4 h-4" />
                  </button>
                </SimpleTooltip>
              </div>
              <Switch
                checked={!!org?.share_config_with_admin}
                disabled={shareConfigMutation.isPending}
                onChange={(e) => shareConfigMutation.mutate(e.target.checked)}
              />
            </div>
            {org?.share_config_with_admin && (
              <p className="mt-3 text-xs text-muted-foreground">
                The platform administrator can currently view and manage your AI configuration, theme, and profile settings.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
